import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from './components/ui/badge';
import { Button } from './components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { 
  Dumbbell, 
  Apple, 
  Camera, 
  Trophy, 
  Sparkles, 
  Flame,
  FileText,
  Filter,
  X,
  Hash
} from 'lucide-react';

import { BACKEND_URL, API } from './config';

// Post type configuration
export const POST_TYPES = [
  { value: 'workout', label: 'Workout', icon: Dumbbell, color: 'blue', emoji: '💪' },
  { value: 'nutrition_tip', label: 'Nutrition', icon: Apple, color: 'green', emoji: '🥗' },
  { value: 'progress_photo', label: 'Progress', icon: Camera, color: 'purple', emoji: '📸' },
  { value: 'challenge', label: 'Challenge', icon: Trophy, color: 'orange', emoji: '🏆' },
  { value: 'transformation', label: 'Transformation', icon: Sparkles, color: 'pink', emoji: '✨' },
  { value: 'motivation', label: 'Motivation', icon: Flame, color: 'red', emoji: '🔥' },
  { value: 'general', label: 'General', icon: FileText, color: 'gray', emoji: '📝' }
];

// Get post type config by value
export const getPostTypeConfig = (postType) => {
  return POST_TYPES.find(t => t.value === postType) || POST_TYPES.find(t => t.value === 'general');
};

// Post Type Badge Component
export const PostTypeBadge = ({ postType, size = 'sm', showIcon = true }) => {
  const config = getPostTypeConfig(postType);
  if (!config) return null;
  
  const Icon = config.icon;
  
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-700 border-blue-200',
    green: 'bg-green-100 text-green-700 border-green-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
    orange: 'bg-orange-100 text-orange-700 border-orange-200',
    pink: 'bg-pink-100 text-pink-700 border-pink-200',
    red: 'bg-red-100 text-red-700 border-red-200',
    gray: 'bg-gray-100 text-gray-700 border-gray-200'
  };
  
  const sizeClasses = {
    xs: 'text-xs px-1.5 py-0.5',
    sm: 'text-sm px-2 py-0.5',
    md: 'text-base px-3 py-1'
  };
  
  return (
    <span 
      className={`inline-flex items-center gap-1 rounded-full border ${colorClasses[config.color]} ${sizeClasses[size]}`}
      data-testid={`post-type-badge-${postType}`}
    >
      {showIcon && <Icon className={size === 'xs' ? 'h-3 w-3' : 'h-4 w-4'} />}
      <span>{config.label}</span>
    </span>
  );
};

// Post Type Selector Component (for create/edit post)
export const PostTypeSelector = ({ value, onChange, disabled = false }) => {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">Post Type</label>
      <Select value={value || 'general'} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid="post-type-selector">
          <SelectValue placeholder="Select post type" />
        </SelectTrigger>
        <SelectContent>
          {POST_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex items-center space-x-2">
                  <Icon className="h-4 w-4" />
                  <span>{type.label}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      <p className="text-xs text-gray-500">
        Categorize your post to help followers find your content
      </p>
    </div>
  );
};

// Post Type Filter Buttons Component
export const PostTypeFilters = ({ activeFilter, onFilterChange, showAll = true }) => {
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Filter className="h-4 w-4 text-gray-400" />
      
      {showAll && (
        <Button
          variant={activeFilter === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => onFilterChange(null)}
          className={activeFilter === null ? 'bg-blue-500 hover:bg-blue-600 text-white' : ''}
          data-testid="filter-all"
        >
          All
        </Button>
      )}
      
      {POST_TYPES.filter(t => t.value !== 'general').map((type) => {
        const Icon = type.icon;
        const isActive = activeFilter === type.value;
        
        const activeClasses = {
          blue: 'bg-blue-500 hover:bg-blue-600 text-white',
          green: 'bg-green-500 hover:bg-green-600 text-white',
          purple: 'bg-purple-500 hover:bg-purple-600 text-white',
          orange: 'bg-orange-500 hover:bg-orange-600 text-white',
          pink: 'bg-pink-500 hover:bg-pink-600 text-white',
          red: 'bg-red-500 hover:bg-red-600 text-white',
          gray: 'bg-gray-500 hover:bg-gray-600 text-white'
        };
        
        return (
          <Button
            key={type.value}
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            onClick={() => onFilterChange(isActive ? null : type.value)}
            className={isActive ? activeClasses[type.color] : ''}
            data-testid={`filter-${type.value}`}
          >
            <Icon className="h-4 w-4 mr-1" />
            {type.label}
          </Button>
        );
      })}
    </div>
  );
};

// Compact Post Type Filter (dropdown version)
export const PostTypeFilterDropdown = ({ activeFilter, onFilterChange }) => {
  return (
    <Select value={activeFilter || 'all'} onValueChange={(v) => onFilterChange(v === 'all' ? null : v)}>
      <SelectTrigger className="w-[180px]" data-testid="post-type-filter-dropdown">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4" />
          <SelectValue placeholder="Filter by type" />
        </div>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">
          <span>All Posts</span>
        </SelectItem>
        {POST_TYPES.map((type) => {
          const Icon = type.icon;
          return (
            <SelectItem key={type.value} value={type.value}>
              <div className="flex items-center space-x-2">
                <Icon className="h-4 w-4" />
                <span>{type.label}</span>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

// Post Type Stats Component (shows count per type)
export const PostTypeStats = ({ posts }) => {
  const stats = POST_TYPES.map(type => ({
    ...type,
    count: posts.filter(p => p.post_type === type.value).length
  })).filter(s => s.count > 0);
  
  if (stats.length === 0) return null;
  
  return (
    <div className="flex flex-wrap gap-3">
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <div 
            key={stat.value}
            className="flex items-center space-x-1 text-sm text-gray-600"
          >
            <Icon className="h-4 w-4" />
            <span>{stat.count}</span>
            <span className="text-gray-400">{stat.label}</span>
          </div>
        );
      })}
    </div>
  );
};

// Hook to fetch posts with type filtering
export const useFilteredPosts = (creatorId, initialFilter = null) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        let url = `${API}/posts?creator_id=${creatorId}`;
        if (activeFilter) {
          url += `&post_type=${activeFilter}`;
        }
        const response = await axios.get(url);
        setPosts(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (creatorId) {
      fetchPosts();
    }
  }, [creatorId, activeFilter]);

  return { posts, loading, error, activeFilter, setActiveFilter };
};

export default PostTypeBadge;

// ============================================================
// FITNESS NICHES — Canonical list with vibrant per-niche colors
// ============================================================

export const FITNESS_NICHES = [
  { id: 'bodybuilding', label: 'Bodybuilding', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  { id: 'powerlifting', label: 'Powerlifting', color: '#9333EA', bg: '#FAF5FF', border: '#E9D5FF' },
  { id: 'strongman', label: 'Strongman', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  { id: 'hyrox', label: 'HYROX', color: '#0891B2', bg: '#ECFEFF', border: '#A5F3FC' },
  { id: 'crossfit', label: 'CrossFit / Functional', color: '#E11D48', bg: '#FFF1F2', border: '#FECDD3' },
  { id: 'yoga', label: 'Yoga', color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  { id: 'pilates', label: 'Pilates', color: '#DB2777', bg: '#FDF2F8', border: '#FBCFE8' },
  { id: 'calisthenics', label: 'Calisthenics', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  { id: 'running', label: 'Running', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
  { id: 'cycling', label: 'Cycling', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  { id: 'mobility', label: 'Mobility', color: '#0D9488', bg: '#F0FDFA', border: '#99F6E4' },
  { id: 'weight_loss', label: 'Weight Loss', color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
  { id: 'muscle_building', label: 'Muscle Building', color: '#4F46E5', bg: '#EEF2FF', border: '#C7D2FE' },
  { id: 'rehab_recovery', label: 'Rehab / Recovery', color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0' },
  { id: 'general_fitness', label: 'General Fitness', color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' },
  { id: 'hiit', label: 'HIIT', color: '#F59E0B', bg: '#FFFBEB', border: '#FDE68A' },
  { id: 'swimming', label: 'Swimming', color: '#06B6D4', bg: '#ECFEFF', border: '#A5F3FC' },
  { id: 'martial_arts', label: 'Martial Arts', color: '#BE123C', bg: '#FFF1F2', border: '#FECDD3' },
];

export const getNicheConfig = (nicheId) => {
  return FITNESS_NICHES.find(n => n.id === nicheId);
};

// NicheBadge — renders a single niche tag with vibrant styling
export const NicheBadge = ({ nicheId, size = 'sm', onRemove }) => {
  const config = getNicheConfig(nicheId);
  if (!config) return null;

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5 gap-0.5',
    sm: 'text-xs px-2 py-0.5 gap-1',
    md: 'text-sm px-2.5 py-1 gap-1',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${sizeClasses[size]} transition-colors`}
      style={{ color: config.color, backgroundColor: config.bg, border: `1px solid ${config.border}` }}
      data-testid={`niche-badge-${nicheId}`}
    >
      <Hash className={size === 'xs' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      {config.label}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(nicheId); }}
          className="ml-0.5 hover:opacity-70"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
};

// NicheSelector — multi-select for choosing niches on a post
export const NicheSelector = ({ selected = [], onChange, max = 3 }) => {
  const [open, setOpen] = useState(false);
  const available = FITNESS_NICHES.filter(n => !selected.includes(n.id));

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
        <Hash className="h-3.5 w-3.5" />
        Fitness Niches
        <span className="text-gray-400 font-normal">({selected.length}/{max})</span>
      </label>

      {/* Selected niches */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map(id => (
            <NicheBadge
              key={id}
              nicheId={id}
              size="sm"
              onRemove={(nid) => onChange(selected.filter(s => s !== nid))}
            />
          ))}
        </div>
      )}

      {/* Add niche dropdown */}
      {selected.length < max && (
        <div className="relative">
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={() => setOpen(!open)}
            className="text-xs"
            data-testid="add-niche-btn"
          >
            <Hash className="h-3 w-3 mr-1" />
            {selected.length === 0 ? 'Add niche tags' : 'Add another'}
          </Button>
          {open && (
            <div className="absolute z-50 mt-1 w-64 max-h-52 overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 py-1">
              {available.map(niche => (
                <button
                  key={niche.id}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                  onClick={() => {
                    onChange([...selected, niche.id]);
                    if (selected.length + 1 >= max) setOpen(false);
                  }}
                  data-testid={`niche-option-${niche.id}`}
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: niche.color }} />
                  {niche.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-gray-500">
        Tag your post to help people discover your content
      </p>
    </div>
  );
};
